import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { LocalizationService, ConfigStateService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';

interface SectionOption {
  name: string;
  code: string;
}

@Component({
    selector: 'performance-insights-settings',
    templateUrl: './real-time-performance-insights-settings.component.html',
    styleUrls: ['./real-time-performance-insights-settings.component.scss'],
    standalone: false
})
export class PerformanceInsightsSettingsComponent implements OnInit {
  settingsForm: FormGroup;
  performanceInsightsEnabled = true;
  customPromptEnabled = false;

  readonly defaultSummaryPrompt = this.localizationService.instant('Settings::PerformanceInsights.DefaultPrompt');

  sectionOptions: SectionOption[] = [
    { name: 'summary', code: 'Summary' },
    { name: 'improvement_areas', code: 'ImprovementAreas' },
    { name: 'follow_up_actions', code: 'FollowUpActions' },
    { name: 'positive_feedbacks', code: 'PositiveFeedbacks' },
    { name: 'negative_feedbacks', code: 'NegativeFeedbacks' },
    { name: 'compliance_with_notifications', code: 'ComplianceWithNotifications' },
  ];

  constructor(
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private config: ConfigStateService,
    private fb: FormBuilder
  ) {
    this.settingsForm = this.fb.group({
      performanceInsightsEnabled: [true],
      reportSections: [['summary']],
      customPromptEnabled: [false],
      summaryPrompt: ['']
    });
  }

  ngOnInit(): void {
    this.loadSettings();

    this.settingsForm.get('performanceInsightsEnabled')?.valueChanges.subscribe(enabled => {
      this.performanceInsightsEnabled = enabled;

      if (!enabled) {
        this.settingsForm.get('reportSections')?.setValue([]);
        this.settingsForm.get('customPromptEnabled')?.setValue(false);
      } else {
        const current = this.settingsForm.get('reportSections')?.value ?? [];
        if (!current.includes('summary')) {
          this.settingsForm.get('reportSections')?.setValue(['summary']);
        }
      }
    });

    this.settingsForm.get('reportSections')?.valueChanges.subscribe(sections => {
      if (this.performanceInsightsEnabled && !sections.includes('summary')) {
        this.settingsForm.get('reportSections')?.setValue(['summary', ...sections]);
      }
    });

    this.settingsForm.get('customPromptEnabled')?.valueChanges.subscribe(enabled => {
      this.customPromptEnabled = enabled;
      
      if (!enabled) {
        this.settingsForm.get('summaryPrompt')?.setValue('');
      }
    });
  }

  private loadSettings(): void {
    const performanceInsightsEnabled = this.config.getSetting('PerformanceInsights.PerformanceInsightsEnabled');
    const sectionList = this.config.getSetting('PerformanceInsights.ReportSections');
    const customPromptEnabled = this.config.getSetting('PerformanceInsights.CustomPromptEnabled');
    const summaryPrompt = this.config.getSetting('PerformanceInsights.SummaryPrompt');

    const isPerformanceInsightsEnabled = performanceInsightsEnabled === 'true';
    const parsedSections = sectionList?.split(';').map(s => s.trim()).filter(s => !!s) ?? [];
    const isCustomPromptEnabled = customPromptEnabled === 'true';
    
    let userPromptText = '';
    if (summaryPrompt && summaryPrompt !== 'true' && summaryPrompt !== 'false') {
      if (summaryPrompt.startsWith(this.defaultSummaryPrompt)) {
        userPromptText = summaryPrompt.substring(this.defaultSummaryPrompt.length).trim();
      } else {
        userPromptText = summaryPrompt;
      }
    }

    this.performanceInsightsEnabled = isPerformanceInsightsEnabled;
    this.customPromptEnabled = isCustomPromptEnabled;

    this.settingsForm.patchValue({
      performanceInsightsEnabled: isPerformanceInsightsEnabled,
      reportSections: isPerformanceInsightsEnabled
        ? parsedSections.length > 0
          ? parsedSections
          : ['summary']
        : [],
      customPromptEnabled: isCustomPromptEnabled,
      summaryPrompt: userPromptText 
    });
  }

  isDisabled(code: string): boolean {
    const selected = this.settingsForm.get('reportSections')?.value ?? [];
    return selected.includes(code);
  }

  removeFromSelectedSections(name: string): void {
    if (name === 'summary') {
      return;
    }
    const selected = this.settingsForm.get('reportSections')?.value ?? [];
    const updated = selected.filter(item => item !== name);
    this.settingsForm.get('reportSections')?.setValue(updated);
  }

  onSubmitSettings(): void {
    if (this.settingsForm.invalid) return;

    const performanceInsightsEnabled = this.settingsForm.get('performanceInsightsEnabled')?.value;
    const sections = this.settingsForm.get('reportSections')?.value ?? [];
    const customPromptEnabled = this.settingsForm.get('customPromptEnabled')?.value;
    
    const userText = this.settingsForm.get('summaryPrompt')?.value || '';
    const summaryPrompt = customPromptEnabled ? 
      (userText ? this.defaultSummaryPrompt + ' ' + userText : this.defaultSummaryPrompt) : 
      '';

    const settingsToSave = [
      {
        settingName: 'PerformanceInsights.PerformanceInsightsEnabled',
        settingValue: performanceInsightsEnabled.toString(),
      },
      {
        settingName: 'PerformanceInsights.ReportSections',
        settingValue: sections.join(';'),
      },
      {
        settingName: 'PerformanceInsights.CustomPromptEnabled',
        settingValue: customPromptEnabled.toString(),
      },
      {
        settingName: 'PerformanceInsights.SummaryPrompt',
        settingValue: summaryPrompt,
      },
    ];

    this.settingService
      .saveSetting(settingsToSave)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
          );
        },
        error: err => {
          this.toastr.error(
            this.localizationService.instant('AbpUi::SaveFailed') || 'Failed to save settings.'
          );
          console.error('Save error:', err);
        },
      });
  }
}